import { ChevronDownIcon } from '@heroicons/react/24/outline';

export const INPUT_CLASS =
    'appearance-none block w-full h-9 px-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-[13px] text-gray-800 focus:outline-none focus:border-gray-800 focus:ring-2 focus:ring-gray-100';

export const CARD_LABEL_CLASS = 'text-[10px] font-bold tracking-wide text-gray-400 uppercase';

interface FieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    required?: boolean;
    placeholder?: string;
    hint?: string;
}

export function Field({ label, value, onChange, required, placeholder, hint }: FieldProps) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-gray-700">
                {label}
                {required ? ' *' : ''}
            </span>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                placeholder={placeholder}
                className={INPUT_CLASS}
            />
            {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
        </label>
    );
}

interface SelectFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: string[];
    // Wording for the "no value" choice - a blank line would read as a typo.
    emptyLabel: string;
    disabled?: boolean;
    hint?: string;
}

// A stored value that the taxonomy no longer offers (a deprecated category)
// stays selectable, so opening an old product never silently rewrites it.
export function SelectField({
    label,
    value,
    onChange,
    options,
    emptyLabel,
    disabled,
    hint
}: SelectFieldProps) {
    const isRetired = value !== '' && !options.includes(value);

    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-gray-700">{label}</span>
            <div className="relative">
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    className={`${INPUT_CLASS} pr-8 bg-white disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${isRetired ? 'text-gray-500' : ''}`}
                >
                    <option value="">{emptyLabel}</option>
                    {isRetired && <option value={value}>{value} (no longer offered)</option>}
                    {options.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
        </label>
    );
}
